import { Button } from "@/components/ui/button";
import { usePode } from "@/lib/permissoes";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Save, Plus, Pencil, Image as ImageIcon, X, Package, Users } from "lucide-react";
import { AdminLista, AdminListaItem, AdminTabela, ALVO_TOQUE } from "@/components/admin/admin-lista";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Parametro, Variedade, Comprador, ProdutoVenda } from "@shared/schema";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { DataCard } from "@/components/data/data-card";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState } from "@/components/data/empty-state";
import { PageHeader } from "@/components/layout/page-header";
import { PageTabs } from "@/components/layout/page-tabs";
import {
  useCompradores, useCreateComprador, useUpdateComprador,
  useProdutosVenda, useCreateProdutoVenda, useUpdateProdutoVenda,
} from "@/hooks/use-vendas";

/**
 * Abas por assunto, uma pergunta cada: "cadastrar um comprador" (Vendas),
 * "trocar o logo do romaneio" (Romaneio), "quantos dias de tolerância"
 * (Produção), "qual a versão" (Sistema). Antes eram três cards empilhados, e
 * um deles — o de Vendas — carregava três assuntos dentro de um card só.
 */
const ABAS = [
  { id: "vendas", label: "Vendas" },
  { id: "romaneio", label: "Romaneio" },
  { id: "producao", label: "Produção" },
  { id: "sistema", label: "Sistema" },
];

const EMISSOR_CAMPOS: Array<{ chave: string; label: string; placeholder: string; grupo: "emissor" | "pagamento" }> = [
  { chave: "EMISSOR_RAZAO_SOCIAL", label: "Razão social", placeholder: "PURASAFRA CULTIVO E COMERCIO LTDA", grupo: "emissor" },
  { chave: "EMISSOR_CNPJ", label: "CNPJ", placeholder: "53.074.246/0001-74", grupo: "emissor" },
  { chave: "EMISSOR_TELEFONE", label: "Telefone", placeholder: "(11) 98485-0301", grupo: "emissor" },
  { chave: "EMISSOR_FAZENDA", label: "Fazenda", placeholder: "ARARA AZUL", grupo: "emissor" },
  // Aparece no cabeçalho do link público do romaneio (Mercado C1) — é como o
  // comprador identifica de onde veio a carga. Sem preenchimento, a linha some.
  { chave: "EMISSOR_CIDADE", label: "Cidade", placeholder: "Ibipeba - BA", grupo: "emissor" },
  { chave: "PAGAMENTO_PIX_CHAVE", label: "Chave PIX", placeholder: "CNPJ, e-mail ou telefone", grupo: "pagamento" },
  { chave: "PAGAMENTO_BANCO", label: "Banco", placeholder: "ITAÚ", grupo: "pagamento" },
  { chave: "PAGAMENTO_AGENCIA", label: "Agência", placeholder: "1268", grupo: "pagamento" },
  { chave: "PAGAMENTO_CONTA", label: "Conta", placeholder: "95179-3", grupo: "pagamento" },
];

const LOGO_MAX_LADO = 256;
const LOGO_MAX_BYTES_APROX = 400 * 1024; // ~400KB de data URL (base64)

/**
 * Larguras em PORCENTAGEM, não em `rem`, e a coluna do nome também declarada.
 *
 * Medido no navegador: com `table-fixed`, a coluna elástica (`null`) recebe o
 * que sobra — e quando as fixas somam mais que o container, o que sobra é
 * NEGATIVO e ela vira **0px**. A conta fecha em 448px: a 768px de viewport, a
 * barra lateral leva 16rem e o `p-8` do `main` leva mais 4rem. Estas seis
 * colunas em `rem` somavam 464px e o Nome desaparecia — exatamente o modo de
 * falha que o `admin-lista.tsx` documenta para grade com coluna fixa em px.
 * Em porcentagem nada zera: o navegador reduz todas na mesma proporção.
 */
const COLUNAS_PRODUTOS = ["26%", "21%", "15%", "15%", "23%", "3.5rem"];
const COLUNAS_COMPRADORES = ["34%", "21%", "21%", "24%", "3.5rem"];

/** Redimensiona no client (canvas) pra no máximo 256×256, mantendo proporção e fundo
 * transparente, e exporta como PNG data URL — evita subir/guardar imagens gigantes
 * num parâmetro de texto. */
function redimensionarLogoParaDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const escala = Math.min(1, LOGO_MAX_LADO / Math.max(img.width, img.height));
      const largura = Math.round(img.width * escala);
      const altura = Math.round(img.height * escala);
      const canvas = document.createElement("canvas");
      canvas.width = largura;
      canvas.height = altura;
      const ctx = canvas.getContext("2d");
      if (!ctx) { reject(new Error("Não foi possível processar a imagem")); return; }
      ctx.clearRect(0, 0, largura, altura);
      ctx.drawImage(img, 0, 0, largura, altura);
      URL.revokeObjectURL(img.src);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Não foi possível ler a imagem"));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Ativo/inativo aqui é as duas coisas: o ESTADO do registro e o controle que
 * vira esse estado. O tom vem do `StatusBadge`, como em qualquer outra tela; o
 * que este componente acrescenta é o alvo clicável em volta, com rótulo
 * acessível dizendo o que o clique faz — o badge sozinho não anuncia ação.
 */
function BotaoAtivo({
  ativo, nome, onToggle, testId,
}: { ativo: boolean; nome: string; onToggle: () => void; testId: string }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={`${ativo ? "Desativar" : "Ativar"} ${nome}`}
      data-testid={testId}
      className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {ativo
        ? <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge>
        : <StatusBadge tom="neutro">Inativo</StatusBadge>}
    </button>
  );
}

function SecaoProdutos() {
  const { toast } = useToast();
  const { data: produtos = [] } = useProdutosVenda();
  const createProduto = useCreateProdutoVenda();
  const updateProduto = useUpdateProdutoVenda();
  const { data: variedades = [] } = useQuery<Variedade[]>({ queryKey: ["/api/variedades"] });

  const [novoProduto, setNovoProduto] = useState({ nome: "", variedadeId: "", classificacao: "", pesoCaixaKg: "" });
  const [editandoProduto, setEditandoProduto] = useState<ProdutoVenda | null>(null);
  const [formProdutoEdit, setFormProdutoEdit] = useState({ nome: "", variedadeId: "", classificacao: "", pesoCaixaKg: "" });

  async function handleAddProduto() {
    if (!novoProduto.nome.trim() || !novoProduto.pesoCaixaKg) return;
    try {
      await createProduto.mutateAsync({
        nome: novoProduto.nome.trim(),
        variedadeId: novoProduto.variedadeId || null,
        classificacao: novoProduto.classificacao.trim() || null,
        pesoCaixaKg: novoProduto.pesoCaixaKg,
      } as any);
      setNovoProduto({ nome: "", variedadeId: "", classificacao: "", pesoCaixaKg: "" });
      toast({ title: "Produto de venda cadastrado!" });
    } catch (e: any) {
      toast({ title: "Erro ao cadastrar produto", description: e.message, variant: "destructive" });
    }
  }

  function abrirEdicaoProduto(p: ProdutoVenda) {
    setEditandoProduto(p);
    setFormProdutoEdit({
      nome: p.nome,
      variedadeId: p.variedadeId ?? "",
      classificacao: p.classificacao ?? "",
      pesoCaixaKg: p.pesoCaixaKg,
    });
  }

  async function handleSalvarProdutoEdit() {
    if (!editandoProduto) return;
    const pesoNum = parseFloat(formProdutoEdit.pesoCaixaKg.replace(",", "."));
    if (!formProdutoEdit.nome.trim() || !(pesoNum > 0)) {
      toast({ title: "Preencha o nome e um Kg/cx válido (maior que zero)", variant: "destructive" });
      return;
    }
    try {
      await updateProduto.mutateAsync({
        id: editandoProduto.id,
        data: {
          nome: formProdutoEdit.nome.trim(),
          variedadeId: formProdutoEdit.variedadeId || null,
          classificacao: formProdutoEdit.classificacao.trim() || null,
          pesoCaixaKg: formProdutoEdit.pesoCaixaKg,
        } as any,
      });
      toast({ title: "Produto atualizado!" });
      setEditandoProduto(null);
    } catch (e: any) {
      toast({ title: "Erro ao salvar produto", description: e.message, variant: "destructive" });
    }
  }

  const nomeVariedade = (id: string | null) => variedades.find(v => v.id === id)?.nome;

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">Produtos de venda</h2>

      {produtos.length === 0 ? (
        <EmptyState
          compacto
          icone={Package}
          titulo="Nenhum produto de venda"
          descricao="O produto é o que a venda lança: nome, variedade e o Kg por caixa que converte caixa em quilo. Cadastre o primeiro abaixo."
          testId="empty-produtos-venda"
        />
      ) : (
        <>
          {/* Mobile: card por produto. A tabela de 6 colunas rolava lateralmente
              em 390px e o Kg/cx — o número que importa — ficava fora da tela. */}
          <AdminLista>
            {produtos.map(p => (
              <AdminListaItem
                key={p.id}
                testId={`produto-item-${p.id}`}
                atenuado={!p.ativo}
                identidade={p.nome}
                meta={
                  <>
                    <span>{nomeVariedade(p.variedadeId) ?? "sem variedade"}</span>
                    {p.classificacao && <span>· {p.classificacao}</span>}
                    <span className="tabular-nums">· {p.pesoCaixaKg} kg/cx</span>
                  </>
                }
                acoes={
                  <>
                    <BotaoAtivo
                      ativo={p.ativo}
                      nome={p.nome}
                      onToggle={() => updateProduto.mutate({ id: p.id, data: { ativo: !p.ativo } })}
                      testId={`button-ativo-produto-${p.id}`}
                    />
                    <Button
                      variant="ghost" size="icon" className={ALVO_TOQUE}
                      aria-label={`Editar ${p.nome}`}
                      onClick={() => abrirEdicaoProduto(p)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </>
                }
              />
            ))}
          </AdminLista>

          <AdminTabela>
            <DataTable colunas={COLUNAS_PRODUTOS}>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Nome</DataTableHead>
                  <DataTableHead>Variedade</DataTableHead>
                  <DataTableHead>Classe</DataTableHead>
                  <DataTableHead num>Kg/cx</DataTableHead>
                  <DataTableHead>Situação</DataTableHead>
                  <DataTableHead />
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {produtos.map(p => (
                  <DataTableRow key={p.id} data-testid={`row-produto-${p.id}`}>
                    <DataTableCell destaque className={`truncate ${!p.ativo ? "opacity-50" : ""}`}>
                      {p.nome}
                    </DataTableCell>
                    <DataTableCell className={`text-muted-foreground truncate ${!p.ativo ? "opacity-50" : ""}`}>
                      {nomeVariedade(p.variedadeId) ?? "—"}
                    </DataTableCell>
                    <DataTableCell className={`truncate ${!p.ativo ? "opacity-50" : ""}`}>
                      {p.classificacao ?? "—"}
                    </DataTableCell>
                    <DataTableNum className={!p.ativo ? "opacity-50" : ""}>{p.pesoCaixaKg}</DataTableNum>
                    <DataTableCell>
                      <BotaoAtivo
                        ativo={p.ativo}
                        nome={p.nome}
                        onToggle={() => updateProduto.mutate({ id: p.id, data: { ativo: !p.ativo } })}
                        testId={`button-ativo-produto-desktop-${p.id}`}
                      />
                    </DataTableCell>
                    <DataTableCell className="text-right">
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7"
                        aria-label={`Editar ${p.nome}`}
                        onClick={() => abrirEdicaoProduto(p)}
                        data-testid={`button-editar-produto-${p.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
          </AdminTabela>
        </>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Input placeholder="Nome (ex: Banana Prata 1ª)" value={novoProduto.nome} onChange={e => setNovoProduto(f => ({ ...f, nome: e.target.value }))} className="md:col-span-2" />
        <Select value={novoProduto.variedadeId} onValueChange={v => setNovoProduto(f => ({ ...f, variedadeId: v }))}>
          <SelectTrigger><SelectValue placeholder="Variedade" /></SelectTrigger>
          <SelectContent>
            {variedades.map(v => <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Classe (1ª...)" value={novoProduto.classificacao} onChange={e => setNovoProduto(f => ({ ...f, classificacao: e.target.value }))} />
        <div className="flex gap-2">
          <Input placeholder="Kg/cx" type="number" step="0.01" value={novoProduto.pesoCaixaKg} onChange={e => setNovoProduto(f => ({ ...f, pesoCaixaKg: e.target.value }))} />
          <Button size="icon" onClick={handleAddProduto} disabled={createProduto.isPending} aria-label="Adicionar produto"><Plus className="h-4 w-4" /></Button>
        </div>
      </div>

      <Dialog open={!!editandoProduto} onOpenChange={(open) => !open && setEditandoProduto(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar produto de venda</DialogTitle>
            <DialogDescription>Alterações valem para novas vendas — itens de vendas já lançadas não mudam.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Nome</Label>
              <Input value={formProdutoEdit.nome} onChange={e => setFormProdutoEdit(f => ({ ...f, nome: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Variedade</Label>
              <Select
                value={formProdutoEdit.variedadeId || "__nenhuma__"}
                onValueChange={v => setFormProdutoEdit(f => ({ ...f, variedadeId: v === "__nenhuma__" ? "" : v }))}
              >
                <SelectTrigger><SelectValue placeholder="Variedade" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__nenhuma__">Sem variedade</SelectItem>
                  {variedades.map(v => <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Classe</Label>
              <Input value={formProdutoEdit.classificacao} onChange={e => setFormProdutoEdit(f => ({ ...f, classificacao: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Kg/cx</Label>
              <Input
                type="number" step="0.01"
                value={formProdutoEdit.pesoCaixaKg}
                onChange={e => setFormProdutoEdit(f => ({ ...f, pesoCaixaKg: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditandoProduto(null)}>Cancelar</Button>
            <Button onClick={handleSalvarProdutoEdit} disabled={updateProduto.isPending}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function SecaoCompradores() {
  const { toast } = useToast();
  const { data: compradores = [] } = useCompradores();
  const createComprador = useCreateComprador();
  const updateComprador = useUpdateComprador();

  const [novoComprador, setNovoComprador] = useState({ nome: "", telefone: "", cidade: "" });
  const [editandoComprador, setEditandoComprador] = useState<Comprador | null>(null);
  const [formCompradorEdit, setFormCompradorEdit] = useState({ nome: "", telefone: "", cidade: "" });

  async function handleAddComprador() {
    if (!novoComprador.nome.trim()) return;
    try {
      await createComprador.mutateAsync({
        nome: novoComprador.nome.trim(),
        telefone: novoComprador.telefone.trim() || undefined,
        cidade: novoComprador.cidade.trim() || undefined,
      });
      setNovoComprador({ nome: "", telefone: "", cidade: "" });
      toast({ title: "Comprador cadastrado!" });
    } catch (e: any) {
      toast({ title: "Erro ao cadastrar comprador", description: e.message, variant: "destructive" });
    }
  }

  function abrirEdicaoComprador(c: Comprador) {
    setEditandoComprador(c);
    setFormCompradorEdit({ nome: c.nome, telefone: c.telefone ?? "", cidade: c.cidade ?? "" });
  }

  async function handleSalvarCompradorEdit() {
    if (!editandoComprador) return;
    if (!formCompradorEdit.nome.trim()) {
      toast({ title: "Informe o nome do comprador", variant: "destructive" });
      return;
    }
    try {
      await updateComprador.mutateAsync({
        id: editandoComprador.id,
        data: {
          nome: formCompradorEdit.nome.trim(),
          telefone: formCompradorEdit.telefone.trim() || null,
          cidade: formCompradorEdit.cidade.trim() || null,
        } as any,
      });
      toast({ title: "Comprador atualizado!" });
      setEditandoComprador(null);
    } catch (e: any) {
      toast({ title: "Erro ao salvar comprador", description: e.message, variant: "destructive" });
    }
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">Compradores</h2>

      {compradores.length === 0 ? (
        <EmptyState
          compacto
          icone={Users}
          titulo="Nenhum comprador"
          descricao="É o comprador que a venda escolhe e o romaneio identifica. Cadastre o primeiro abaixo."
          testId="empty-compradores"
        />
      ) : (
        <>
          <AdminLista>
            {compradores.map(c => (
              <AdminListaItem
                key={c.id}
                testId={`comprador-item-${c.id}`}
                atenuado={!c.ativo}
                identidade={c.nome}
                meta={
                  <>
                    <span>{c.telefone ?? "sem telefone"}</span>
                    {c.cidade && <span>· {c.cidade}</span>}
                  </>
                }
                acoes={
                  <>
                    <BotaoAtivo
                      ativo={c.ativo}
                      nome={c.nome}
                      onToggle={() => updateComprador.mutate({ id: c.id, data: { ativo: !c.ativo } })}
                      testId={`button-ativo-comprador-${c.id}`}
                    />
                    <Button
                      variant="ghost" size="icon" className={ALVO_TOQUE}
                      aria-label={`Editar ${c.nome}`}
                      onClick={() => abrirEdicaoComprador(c)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </>
                }
              />
            ))}
          </AdminLista>

          <AdminTabela>
            <DataTable colunas={COLUNAS_COMPRADORES}>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Nome</DataTableHead>
                  <DataTableHead>Telefone</DataTableHead>
                  <DataTableHead>Cidade</DataTableHead>
                  <DataTableHead>Situação</DataTableHead>
                  <DataTableHead />
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {compradores.map(c => (
                  <DataTableRow key={c.id} data-testid={`row-comprador-${c.id}`}>
                    <DataTableCell destaque className={`truncate ${!c.ativo ? "opacity-50" : ""}`}>
                      {c.nome}
                    </DataTableCell>
                    <DataTableCell className={`text-muted-foreground truncate ${!c.ativo ? "opacity-50" : ""}`}>
                      {c.telefone ?? "—"}
                    </DataTableCell>
                    <DataTableCell className={`text-muted-foreground truncate ${!c.ativo ? "opacity-50" : ""}`}>
                      {c.cidade ?? "—"}
                    </DataTableCell>
                    <DataTableCell>
                      <BotaoAtivo
                        ativo={c.ativo}
                        nome={c.nome}
                        onToggle={() => updateComprador.mutate({ id: c.id, data: { ativo: !c.ativo } })}
                        testId={`button-ativo-comprador-desktop-${c.id}`}
                      />
                    </DataTableCell>
                    <DataTableCell className="text-right">
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7"
                        aria-label={`Editar ${c.nome}`}
                        onClick={() => abrirEdicaoComprador(c)}
                        data-testid={`button-editar-comprador-${c.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
          </AdminTabela>
        </>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Input placeholder="Nome" value={novoComprador.nome} onChange={e => setNovoComprador(f => ({ ...f, nome: e.target.value }))} />
        <Input placeholder="Telefone" value={novoComprador.telefone} onChange={e => setNovoComprador(f => ({ ...f, telefone: e.target.value }))} />
        <Input placeholder="Cidade" value={novoComprador.cidade} onChange={e => setNovoComprador(f => ({ ...f, cidade: e.target.value }))} />
        <Button onClick={handleAddComprador} disabled={createComprador.isPending}><Plus className="h-4 w-4 mr-1" />Adicionar</Button>
      </div>

      <Dialog open={!!editandoComprador} onOpenChange={(open) => !open && setEditandoComprador(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar comprador</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Nome</Label>
              <Input value={formCompradorEdit.nome} onChange={e => setFormCompradorEdit(f => ({ ...f, nome: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Telefone</Label>
              <Input value={formCompradorEdit.telefone} onChange={e => setFormCompradorEdit(f => ({ ...f, telefone: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Cidade</Label>
              <Input value={formCompradorEdit.cidade} onChange={e => setFormCompradorEdit(f => ({ ...f, cidade: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditandoComprador(null)}>Cancelar</Button>
            <Button onClick={handleSalvarCompradorEdit} disabled={updateComprador.isPending}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function SecaoRomaneio({ parametros }: { parametros: Parametro[] | undefined }) {
  const { toast } = useToast();
  const [emissorForm, setEmissorForm] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!parametros) return;
    const values: Record<string, string> = {};
    for (const campo of EMISSOR_CAMPOS) {
      values[campo.chave] = parametros.find(p => p.chave === campo.chave)?.valor ?? "";
    }
    setEmissorForm(values);
  }, [parametros]);

  const upsertParametro = useMutation({
    mutationFn: ({ chave, valor }: { chave: string; valor: string }) =>
      apiRequest("POST", "/api/parametros/upsert", { chave, valor }).then(r => r.json()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/parametros"] }),
  });

  async function handleSalvarEmissor() {
    try {
      for (const campo of EMISSOR_CAMPOS) {
        await upsertParametro.mutateAsync({ chave: campo.chave, valor: emissorForm[campo.chave] ?? "" });
      }
      toast({ title: "Dados de venda/romaneio salvos!" });
    } catch (e: any) {
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    }
  }

  const logoDataUrl = parametros?.find(p => p.chave === "EMISSOR_LOGO")?.valor || "";

  async function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await redimensionarLogoParaDataUrl(file);
      if (dataUrl.length > LOGO_MAX_BYTES_APROX) {
        toast({ title: "Imagem muito grande", description: "Escolha uma imagem mais simples ou menor.", variant: "destructive" });
        return;
      }
      await upsertParametro.mutateAsync({ chave: "EMISSOR_LOGO", valor: dataUrl });
      toast({ title: "Logo atualizado!" });
    } catch (err: any) {
      toast({ title: "Erro ao processar a imagem", description: err.message, variant: "destructive" });
    } finally {
      e.target.value = "";
    }
  }

  async function handleRemoverLogo() {
    try {
      await upsertParametro.mutateAsync({ chave: "EMISSOR_LOGO", valor: "" });
      toast({ title: "Logo removido." });
    } catch (err: any) {
      toast({ title: "Erro ao remover o logo", description: err.message, variant: "destructive" });
    }
  }

  function grupoDeCampos(grupo: "emissor" | "pagamento") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {EMISSOR_CAMPOS.filter(c => c.grupo === grupo).map(campo => (
          <div key={campo.chave} className="space-y-1">
            <label className="text-xs text-muted-foreground">{campo.label}</label>
            <Input
              placeholder={campo.placeholder}
              value={emissorForm[campo.chave] ?? ""}
              onChange={e => setEmissorForm(f => ({ ...f, [campo.chave]: e.target.value }))}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Logo da fazenda</h2>
        <div className="flex items-center gap-3">
          {logoDataUrl ? (
            <>
              <img src={logoDataUrl} alt="Logo da fazenda" className="h-16 w-16 rounded-full object-cover border" />
              <Button variant="outline" size="sm" onClick={handleRemoverLogo} disabled={upsertParametro.isPending}>
                <X className="h-3.5 w-3.5 mr-1" />Remover
              </Button>
            </>
          ) : (
            <div className="h-16 w-16 rounded-full border flex items-center justify-center bg-muted">
              <ImageIcon className="h-6 w-6 text-muted-foreground" />
            </div>
          )}
          <div className="space-y-1">
            <Input
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleLogoChange}
              className="max-w-xs"
              data-testid="input-logo-fazenda"
            />
            <p className="text-xs text-muted-foreground">Aparece no cabeçalho do romaneio (PDF e imagem).</p>
          </div>
        </div>
      </section>

      {/* Os nove campos eram uma grade só, com a chave PIX logo depois da cidade.
          São dois assuntos: quem emite e para onde se paga. */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Identificação do emissor</h2>
        {grupoDeCampos("emissor")}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Pagamento</h2>
        {grupoDeCampos("pagamento")}
      </section>

      <Button onClick={handleSalvarEmissor} disabled={upsertParametro.isPending} data-testid="button-salvar-romaneio">
        <Save className="h-4 w-4 mr-2" />Salvar dados do romaneio
      </Button>
    </div>
  );
}

/**
 * Painel de aba. **Esconde, não desmonta** — e é de propósito: todo painel
 * daqui é formulário, e o do romaneio tem nove campos. Desmontar ao trocar de
 * aba jogaria fora o que foi digitado e não salvo, coisa que a tela empilhada
 * de antes não fazia. Aba é organização, não perda de dado.
 */
function Painel({ ativo, children, testId }: { ativo: boolean; children: React.ReactNode; testId: string }) {
  return (
    <div hidden={!ativo} data-testid={testId} className="space-y-8">
      {children}
    </div>
  );
}

export default function ConfiguracoesPage() {
  const { toast } = useToast();
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  // PR 2: configurar a org é `org.configurar` (só ADMIN) — pela matriz, não pelo papel cru.
  const isAdmin = usePode("org.configurar");

  // A rota já exige `cap="org.configurar"` (App.tsx) — quem não pode não chega
  // aqui. O `isAdmin` continua como segunda tranca, e decide também QUAIS abas
  // existem: aba que abre vazia é pior do que aba que não aparece.
  const abas = isAdmin ? ABAS : ABAS.filter(a => a.id === "sistema");
  const [aba, setAba] = useState(abas[0].id);

  const { data: parametros } = useQuery<Parametro[]>({
    queryKey: ["/api/parametros"],
  });

  const { data: appVersionData } = useQuery<{ versao: string | null }>({
    queryKey: ["/api/app-version"],
  });

  const toleranciaParam = parametros?.find(p => p.chave === "TOLERANCIA_DIAS_COLHEITA");
  const [toleranciaDias, setToleranciaDias] = useState<string>("");

  useEffect(() => {
    if (toleranciaParam) {
      setToleranciaDias(toleranciaParam.valor);
    }
  }, [toleranciaParam]);

  // Upsert, não PATCH: o parâmetro pode nunca ter sido criado nesta org — quem lê
  // sem encontrar usa o default de 15 dias (server/lib/tolerancia.ts).
  const salvarTolerancia = useMutation({
    mutationFn: (valor: string) =>
      apiRequest("POST", "/api/parametros/upsert", {
        chave: "TOLERANCIA_DIAS_COLHEITA",
        valor,
        tipo: "INTEGER",
      }).then(r => r.json()),
    onSuccess: () => {
      toast({ title: "Sucesso", description: "Tolerância atualizada com sucesso" });
      queryClient.invalidateQueries({ queryKey: ["/api/parametros"] });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Erro ao atualizar configuração",
        variant: "destructive",
      });
    },
  });

  const handleSaveTolerancia = () => {
    const dias = parseInt(toleranciaDias, 10);
    if (isNaN(dias) || dias < 1) {
      toast({
        title: "Erro",
        description: "Digite um número válido de dias (mínimo 1)",
        variant: "destructive",
      });
      return;
    }
    salvarTolerancia.mutate(String(dias));
  };

  return (
    <div className="space-y-6">
      <PageHeader titulo="Configurações" descricao="Gerenciamento do sistema" />

      {abas.length > 1 && <PageTabs abas={abas} ativa={aba} onChange={setAba} />}

      {isAdmin && (
        <>
          <Painel ativo={aba === "vendas"} testId="painel-vendas">
            <SecaoProdutos />
            <SecaoCompradores />
          </Painel>

          <Painel ativo={aba === "romaneio"} testId="painel-romaneio">
            <SecaoRomaneio parametros={parametros} />
          </Painel>

          <Painel ativo={aba === "producao"} testId="painel-producao">
            <section className="space-y-3">
              <h2 className="text-sm font-semibold">Colheita</h2>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">
                    Tolerância de dias para colheita
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={toleranciaDias}
                    onChange={(e) => setToleranciaDias(e.target.value)}
                    placeholder="15"
                    className="w-full md:w-48"
                    data-testid="input-tolerancia-dias"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Número de dias de tolerância para considerar colheita no prazo. Sem valor definido, o sistema usa 15 dias.
                  </p>
                </div>
                <Button
                  onClick={handleSaveTolerancia}
                  disabled={!toleranciaDias || toleranciaDias === toleranciaParam?.valor || salvarTolerancia.isPending}
                  data-testid="button-save-tolerancia"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Salvar
                </Button>
              </div>
            </section>
          </Painel>
        </>
      )}

      <Painel ativo={aba === "sistema"} testId="painel-sistema">
        <DataCard padded>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Versão</span>
              <span className="font-mono font-medium" data-testid="text-versao">
                {appVersionData?.versao || "—"}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Ambiente</span>
              <span className="font-medium">
                {import.meta.env.PROD ? "Produção" : "Desenvolvimento"}
              </span>
            </div>
          </div>
        </DataCard>
      </Painel>
    </div>
  );
}
